import { Injectable, inject } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { NavController } from "@ionic/angular";
import { debounceTime, filter, interval, merge } from "rxjs";
import { flightsOver } from "../../pages/med-crew/flights-over";
import { activeFlight } from "../../pages/med-crew/med-crew-view";
import { classCom } from "../../plugins/opsctrl-foc/focMain";
import { SyncChangesService } from "../../sync/sync-changes.service";
import { opsControlService } from "../ops-control/ops-control.service";
import { isAutoAcceptIdleRoute } from "./mission-popups";
import { MissionService } from "./mission.service";
import { AutoNavigService } from "./auto-navig.service";
import { flightRouteFor, idleRouteFor, navigAtLoad, scrollFlightFor } from "./wallboard-idle";

const FLIGHT_WINDOW_MS = 24 * 3600 * 1000;
const RECHECK_MS = 30000;

/**
 * Wallboard windows named `efb-auto-navig` and `efb-auto-accept`: with no active
 * flight the window opens its waiting tab (webcams, weather). When a flight
 * starts, the weather window opens the hybrid tab. Does nothing in
 * any other window. The next flight leaves the tab by itself: auto-NAVIG opens
 * the map, and a flight create ends on the flow briefing.
 */
@Injectable({ providedIn: "root" })
export class WallboardIdleService {
  private router = inject(Router);
  private navCtrl = inject(NavController);
  private opsCtrlSer = inject(opsControlService);
  private syncChanges = inject(SyncChangesService);
  private missions = inject(MissionService);
  private autoNavig = inject(AutoNavigService);

  private hadFlight: boolean | undefined;
  private shownFlight: string | undefined;
  private checking = false;
  /** True while a mission would create no flight: no signed, valid duty with a PIC.
   *  The app shell shows a warning bar for it. */
  public noDuty = false;

  constructor() {
    if (typeof window === "undefined" || idleRouteFor(window.name, undefined, false) === null) return;
    // ponytail: the timer only repeats the check, for a read that failed before
    // login and for a flight that ended while the window was on another page.
    merge(
      this.syncChanges.changes("flightlist"),
      this.router.events.pipe(filter((e) => e instanceof NavigationEnd)),
      interval(RECHECK_MS)
    )
      .pipe(debounceTime(1000))
      .subscribe(() => {
        void this.check();
        void this.checkDuty();
      });
    this.syncChanges.changes("flightcrew").subscribe(() => void this.checkDuty());
  }

  /** Not behind `mayMove`: the warning must show on every page of the window.
   *  ponytail: polled every 30 s, so a duty that runs out shows up to 30 s late. */
  private async checkDuty(): Promise<void> {
    // Not on the login page, and not on the page that signs the duty.
    if (this.router.url.startsWith("/login") || this.router.url.startsWith("/duty-ofp-limits")) {
      this.noDuty = false;
      return;
    }
    try {
      this.noDuty = !(await this.opsCtrlSer.missionDutyReady());
    } catch (err) {
      // A failed read is not a missing duty: keep what the bar shows.
      console.warn("[wallboardIdle] duty check failed:", err);
    }
  }

  /** Only from the pages the window rests on, and never under a form, a route
   *  edit, a running create or a pending auto-accept. ponytail: an open dialog
   *  is not closed. */
  private mayMove(): boolean {
    return (
      isAutoAcceptIdleRoute(this.router.url) &&
      !this.opsCtrlSer.flightCreateRunning &&
      !this.opsCtrlSer.MapInEditMode &&
      this.missions.autoPendingId === undefined
    );
  }

  private async check(): Promise<void> {
    if (this.checking || !this.mayMove()) return;
    this.checking = true;
    try {
      const maxTs = Date.now();
      const rows = await classCom.namedQuery("flightlistInCreationRange", { minTs: maxTs - FLIGHT_WINDOW_MS, maxTs });
      const over = await flightsOver(rows ?? [], maxTs);
      // The reads are network hops on web. A form, a create or a mission's
      // auto-accept can have started meanwhile, so ask again and try later.
      if (!this.mayMove()) return;
      const flight = activeFlight(rows ?? [], over);
      const hasFlight = !!flight;
      const path = this.router.url.split("?")[0];
      const route =
        idleRouteFor(window.name, this.hadFlight, hasFlight) ??
        flightRouteFor(window.name, this.hadFlight, hasFlight, path);
      if (navigAtLoad(window.name, this.hadFlight, hasFlight)) this.autoNavig.open(String(flight.flightId));
      const had = this.hadFlight;
      this.hadFlight = hasFlight;
      const activeId = flight ? String(flight.flightId) : undefined;
      const scrollTo = scrollFlightFor(window.name, this.shownFlight, activeId, route ?? path);
      // Wait until the hybrid tab lists the flight: its scroll request repeats
      // until the panel exists. The next check asks again.
      const listed = this.opsCtrlSer.briefingFlowContent_forPageHtml?.some((p) => p.data.flightId === scrollTo);
      if (!activeId) this.shownFlight = undefined;
      else if (scrollTo && listed) {
        this.shownFlight = activeId;
        this.opsCtrlSer.flightAdded_allowScrolling_targetFlight = scrollTo;
        this.opsCtrlSer.flightAdded_allowScrolling = true;
      }
      // A move that did not happen is tried again at the next check.
      if (route && path !== route && !(await this.navCtrl.navigateRoot(route).catch(() => false))) this.hadFlight = had;
    } catch (err) {
      console.warn("[wallboardIdle] check failed:", err);
    } finally {
      this.checking = false;
    }
  }
}
